//React
import { FunctionComponent, useEffect, useState } from "react";

//Models
import { RangeFacet } from "../../models/facets/range-facet";
import { RangeValues } from "../../models/types/type-range-values";

//Radix UI
import * as Slider from "@radix-ui/react-slider";

//App Hooks
import {
	useAppDispatch,
	useAppSelector,
} from "../../../system/redux/hooks/redux-store-hooks";

//Selectors
import { selectVisibleRangeFacetValue } from "../../redux/selectors/select-visible-range-facet-value";
import { selectSelectedFacets } from "../../redux/selectors/select-selected-facets";

//Actions
import { setFacetValue } from "../../redux/slices/facet-slice";

interface FacetProps {
	drhFacet: RangeFacet;
	controlId: string;
	formatter: Intl.NumberFormat;
}

const SliderFacet: FunctionComponent<FacetProps> = (props) => {
	const dispatch = useAppDispatch();
	const { drhFacet, controlId, formatter } = props;

	//State Values
	const visibleFacetValueRange = useAppSelector(
		selectVisibleRangeFacetValue(drhFacet.fieldName)
	) as RangeValues;

	const selectedFacetValues = useAppSelector(selectSelectedFacets);

	//Currently selected values
	const [selectedValueLow, setSelectedValueLow] = useState(0);
	const [selectedValueHigh, setSelectedValueHigh] = useState(1);

	const [isLoaded, setIsLoaded] = useState(false);

	const [isDragging, setIsDragging] = useState(false);

	useEffect(() => {
		if (!visibleFacetValueRange) return;

		const currentlySelected = selectedFacetValues[drhFacet.fieldName];

		if (!currentlySelected) {
			setSelectedValueLow(visibleFacetValueRange[0]);
			setSelectedValueHigh(visibleFacetValueRange[1]);
		} else {
			setSelectedValueLow(currentlySelected[0]);
			setSelectedValueHigh(currentlySelected[1]);
		}

		setIsLoaded(true);
	}, [visibleFacetValueRange]);

	useEffect(() => {
		if (!isLoaded) return;

		const currentlySelected = selectedFacetValues[drhFacet.fieldName];
		if (currentlySelected) return;

		setSelectedValueLow(visibleFacetValueRange[0]);
		setSelectedValueHigh(visibleFacetValueRange[1]);
	}, [selectedFacetValues]);

	const updateFacetValues = (values: [number, number]) => {
		dispatch(
			setFacetValue({
				facet: drhFacet,
				facetValues: [values[0], values[1]] as RangeValues,
			})
		);

		setSelectedValueLow(values[0]);
		setSelectedValueHigh(values[1]);
	};

	// Determine if range is selected (different from full range)
	const isSelected =
		selectedValueLow !== visibleFacetValueRange[0] ||
		selectedValueHigh !== visibleFacetValueRange[1];

	return (
		<span id={controlId}>
			{!isLoaded ? (
				<div>Loading...</div>
			) : (
				<div
					className={`CoveoFacetSliderForRange CoveoFacetSlider ${
						isSelected ? "range-slider-active" : ""
					}`}
				>
					<div className="title">
						<h3>{drhFacet.title}</h3>
					</div>

					<div className="coveo-slider-container">
						<Slider.Root
							className={`SliderRoot ${isDragging ? "slider-dragging" : ""}`}
							min={visibleFacetValueRange[0]}
							max={visibleFacetValueRange[1]}
							value={[selectedValueLow, selectedValueHigh]}
							step={50}
							minStepsBetweenThumbs={10}
							onValueChange={updateFacetValues}
							onValueCommit={() => setIsDragging(false)}
							onMouseDown={() => setIsDragging(true)}
							id={controlId}
						>
							<Slider.Track className="coveo-slider-line">
								<Slider.Range className="CustomSliderRange" />
							</Slider.Track>
							<Slider.Thumb className="coveo-slider-button" aria-label="ValueLow" />
							<Slider.Thumb className="coveo-slider-button" aria-label="ValueHigh" />
						</Slider.Root>
					</div>

					<div className="CoveoFacetSliderRange">
						{formatter.format(selectedValueLow)} - {formatter.format(selectedValueHigh)}
					</div>
				</div>
			)}
		</span>
	);
};

export default SliderFacet;
